import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { STAGES, symbolKey } from '../clock/clockModel';

// The Desk's bottom strip (DWLF-300; canvas board AppDesk): the watchlist counted by cycle stage,
// from the Cycle Clock's own model. Clicking a stage steps to the next market in it after the one
// on the chart, so "show me the next overdue one" is one click.

const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' };
const ORDER = ['confirmed_setup', 'awaiting_confirmation', 'window_open', 'early_window', 'overdue', 'missed', 'in_cycle', 'none'];

/** Markets grouped by stage key, in watchlist order. Pure, so the counts are tested. */
export const groupByStage = (markets) => {
  const by = {};
  (markets || []).forEach((m) => {
    const key = m.stage?.key;
    if (!key) return;
    (by[key] = by[key] || []).push(m.symbol);
  });
  return by;
};

/** The next symbol in `list` after `current` (wrapping); the first when `current` isn't in it. */
export const nextInStage = (list, current) => {
  if (!list?.length) return null;
  const i = list.findIndex((s) => symbolKey(s) === symbolKey(current));
  return list[(i + 1) % list.length];
};

export default function StageStrip({ markets, currentSymbol, onSelectSymbol }) {
  const by = groupByStage(markets);
  // A stage ORDER does not name goes on the end, so a new one never drops off the strip.
  const ids = [...ORDER, ...Object.keys(STAGES).filter((id) => id !== 'error' && !ORDER.includes(id))];
  const stages = ids.map((id) => STAGES[id]).filter((st) => by[st.key]?.length);
  if (!stages.length) return null;
  return (
    <Box component="nav" aria-label="Watchlist by cycle stage"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minHeight: 34, px: 1.5, overflowX: 'auto',
        borderTop: `1px solid ${tok('ink-08')}`, backgroundColor: tok('surface') }}>
      <Typography component="span" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-50'), mr: 1, whiteSpace: 'nowrap' }}>Watchlist</Typography>
      {stages.map((st) => {
        const list = by[st.key];
        const here = list.some((s) => symbolKey(s) === symbolKey(currentSymbol));
        const next = nextInStage(list, currentSymbol);
        return (
          <ButtonBase key={st.key} onClick={() => onSelectSymbol(next)}
            title={`${list.length} ${st.label.toLowerCase()}: ${list.join(', ')}. Go to ${next}`}
            sx={{ height: 26, px: 1, gap: 0.75, whiteSpace: 'nowrap', border: `1px solid ${here ? tok('ink-20') : 'transparent'}`,
              '&:hover, &.Mui-focusVisible': { backgroundColor: tok('ink-06') } }}>
            <Box component="span" aria-hidden sx={{ width: 6, height: 6, backgroundColor: tok(st.token) }} />
            <Typography component="span" sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.75rem', color: tok('text') }}>{list.length}</Typography>
            <Typography component="span" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-60') }}>{st.label}</Typography>
          </ButtonBase>
        );
      })}
      <Box sx={{ flex: 1 }} />
      <ButtonBase component={RouterLink} to="/markets" sx={{ ...LABEL, fontSize: '0.5625rem', height: 26, px: 1, color: tok('accent'), whiteSpace: 'nowrap' }}>
        Cycle Clock →
      </ButtonBase>
    </Box>
  );
}
